import { useQuery } from "@apollo/client";
import type { ActivityHit, FacilityHit, PersonHit } from "@/components/AlgoliaSearch";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import {
  navigableGlobalSearchHits,
  toGlobalSearchActivityHit,
  toGlobalSearchFacilityHit,
  toGlobalSearchJobHit,
  toGlobalSearchPersonHit,
  type GlobalSearchEntityHit,
  type JobHit
} from "./mapGlobalSearchHits";
import { GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE, GLOBAL_SEARCH_QUERY, type GlobalSearchHitNode } from "./queries";

const EMPTY_HITS: GlobalSearchEntityHit[] = [];
const EMPTY_PEOPLE: PersonHit[] = [];
const EMPTY_FACILITIES: FacilityHit[] = [];
const EMPTY_JOBS: JobHit[] = [];
const EMPTY_ACTIVITIES: ActivityHit[] = [];
const EMPTY_RECENTLY_VIEWED_IDS: GlobalSearchRecentlyViewedIds = {
  people: [],
  facilities: [],
  jobs: [],
  activities: []
};

export type GlobalSearchRecentlyViewedIds = {
  people: readonly string[];
  facilities: readonly string[];
  jobs: readonly string[];
  activities: readonly string[];
};

export type GlobalSearchEntitySearch = {
  allHits: GlobalSearchEntityHit[];
  peopleHits: PersonHit[];
  facilityHits: FacilityHit[];
  jobHits: JobHit[];
  activityHits: ActivityHit[];
  pending: boolean;
  failed: boolean;
};

function idleSearch(pending: boolean): GlobalSearchEntitySearch {
  return {
    allHits: EMPTY_HITS,
    peopleHits: EMPTY_PEOPLE,
    facilityHits: EMPTY_FACILITIES,
    jobHits: EMPTY_JOBS,
    activityHits: EMPTY_ACTIVITIES,
    pending,
    failed: false
  };
}

function toGlobalSearchHit(node: GlobalSearchHitNode): GlobalSearchEntityHit {
  switch (node.__typename) {
    case "BackofficePerson":
      return toGlobalSearchPersonHit(node);
    case "BackofficeFacility":
      return toGlobalSearchFacilityHit(node);
    case "BackofficeJobPost":
      return toGlobalSearchJobHit(node);
    case "Activity":
      return toGlobalSearchActivityHit(node);
  }
}

export function useGlobalSearchEntitySearch(
  query: string,
  enabled: boolean,
  recentlyViewedIds: GlobalSearchRecentlyViewedIds = EMPTY_RECENTLY_VIEWED_IDS
): GlobalSearchEntitySearch {
  const trimmed = query.trim();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const canViewJobs = useHasCapabilityBool({ domain: DOMAIN.JOBS, permission: PERMISSION.viewJobPosts });
  const canManageJobs = useHasCapabilityBool({ domain: DOMAIN.JOBS, permission: PERMISSION.manageJobPosts });
  const canViewActivities = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.manageEvents
  });

  const canSearchAny = canViewPeople || canViewFacilities || canViewJobs || canManageJobs || canViewActivities;
  const skip = !enabled || !trimmed || !canSearchAny;
  const searchQuery = useQuery(GLOBAL_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      firstPerType: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      recentlyViewedIds: {
        people: recentlyViewedIds.people as string[],
        facilities: recentlyViewedIds.facilities as string[],
        jobs: recentlyViewedIds.jobs as string[],
        activities: recentlyViewedIds.activities as string[]
      },
      withCareer: canViewPersonCareer
    },
    skip,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });

  const queryReady = enabled && Boolean(trimmed) && canSearchAny;
  const pending = queryReady && searchQuery.loading;

  if (!queryReady || searchQuery.loading) {
    return idleSearch(pending);
  }

  const allHits = navigableGlobalSearchHits((searchQuery.data?.globalSearch ?? []).map(toGlobalSearchHit));
  const peopleHits = allHits.filter((hit): hit is PersonHit => hit.object_type === "Person");
  const facilityHits = allHits.filter((hit): hit is FacilityHit => hit.object_type === "Facility");
  const jobHits = allHits.filter((hit): hit is JobHit => hit.object_type === "Job");
  const activityHits = allHits.filter((hit): hit is ActivityHit => hit.object_type === "Activity");

  return {
    allHits,
    peopleHits,
    facilityHits,
    jobHits,
    activityHits,
    pending: false,
    failed: Boolean(searchQuery.error) && allHits.length === 0
  };
}
